import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { Input } from '@/Components/ui/Input';

type DecimalInputProps = Omit<ComponentProps<typeof Input>, 'type' | 'inputMode' | 'value' | 'onChange'> & {
  value?: number | string | null;
  onValueChange: (value: number | null, rawValue: string) => void;
};

const formatDecimal = (value?: number | string | null) => {
  if (value == null || value === '') return '';
  return String(value).replace('.', ',');
};

const parseDecimal = (value: string) => {
  const normalized = value.trim().replace(/\./g, '').replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function DecimalInput({ value, onValueChange, placeholder = '0,00', ...props }: DecimalInputProps) {
  const [text, setText] = useState(formatDecimal(value));
  const lastEmittedValue = useRef<number | string | null | undefined>(value);

  useEffect(() => {
    if (value !== lastEmittedValue.current) {
      setText(formatDecimal(value));
      lastEmittedValue.current = value;
    }
  }, [value]);

  return (
    <Input
      {...props}
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={text}
      onChange={(event) => {
        const nextText = event.target.value;
        const parsed = parseDecimal(nextText);
        setText(nextText);
        lastEmittedValue.current = parsed;
        onValueChange(parsed, nextText);
      }}
    />
  );
}
